// The job page, round 2 (pages/job-page-r2, approved 26 Oct): Blueprint's job record (@flowology/ui-web/
// job's JobRecord) drawn from the round-1 view (job-view-model.ts's JobView) and the server's one read
// (jobs.page). What didn't change (the review, parts, the invoice, the side, the menu's actions) is the
// JobView's, so every control keeps the `can` that allowed it; what round 2 asked for is built here: the
// visits and forms as rows, estimates as cards, the notes and messages as their own thread, the strip's
// stops with who and how, and History's rows, each opening in place with only what its line doesn't say
// (Amir's pick, 26 Oct: pages/job-history-option). Round 3 (Amir's notes, 26 Oct): visits and forms open
// in place as History's rows do; one quiet line of type and number under who and where; the strip
// carries the dates and the next booked visit, so the state line keeps only a word it can't say; every
// photo on the job in one roll, newest first, its forms and files in one list under it (the set "All
// photos and files" showed). Pure: no calls, no clock but the one passed in.
import { dateText, timeText } from '@flowology/kernel';
import type { HistoryFilter, KeyValueItem, MiniInvoiceView, Photo } from '@flowology/ui-web/fl';
import type {
  ActionLink,
  DetailSection,
  JobEntry,
  JobRecordView,
  JobView,
  JobVisit,
  RecordDetail,
  RecordEstimate,
  RecordFormRow,
  RecordState,
  RecordViewer,
  RecordVisit,
} from '@flowology/ui-web/job';
import type { JobState } from '@flowology/ui-web/words';
import { formAnswers, money } from '../review/review-model.ts';
import { namesOf, tileOf } from '../shared/photo-display-model.ts';
import { jobFiles } from './photos-model.ts';
import { PAUSE_REASONS } from '../board-model.ts';
import { phoneText } from '../types.ts';
import { plainText } from '../shared/plain-text.ts';
import type { Estimate } from './job-model.ts';
import {
  namedPeople,
  personName,
  pictureOf,
  waitingOf,
  type JobPageData,
  type PageAttachment,
  type ViewOptions,
} from './job-view-model.ts';

type Message = JobRecordView['thread']['messages'][number];
type StripStop = JobRecordView['strip']['stops'][number];

/** History's tabs, round 2: Key moments and All first, then the kinds (a change to a line is Money's). */
export const RECORD_FILTERS: HistoryFilter[] = [
  { id: 'key', label: 'Key moments', keyOnly: true },
  { id: 'all', label: 'All' },
  { id: 'talk', label: 'Calls, texts', kinds: ['call', 'text'] },
  { id: 'note', label: 'Notes', kinds: ['note'] },
  { id: 'visit', label: 'Visits', kinds: ['visit', 'book'] },
  { id: 'photo', label: 'Photos, forms', kinds: ['photo', 'form'] },
  { id: 'money', label: 'Money', kinds: ['estimate', 'money', 'parts'] },
  { id: 'edit', label: 'Changes', kinds: ['edit'] },
];

const lower = (t: string) => t.replace(/^./, (c) => c.toLowerCase());
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const first = (name: string) => name.trim().split(/\s+/)[0] ?? name;
/** The record's day words as the prototype writes them: "Sat Oct 24", not "Sat, Oct 24". */
export const dayWords = (t: string) => t.replace(/^([A-Z][a-z]{2}), /, '$1 ');

/** The people on the job, by first name: who a strip stop can name. */
function peopleOf(d: JobPageData): Set<string> {
  const m = d.more;
  const names = [
    m.booked.by,
    m.completed?.by,
    m.cancelled?.by,
    ...m.visits.flatMap((v) => v.techs.map((t) => t.name)),
    ...(d.job.notes ?? []).map((n) => n.author),
    ...(d.techThread?.messages ?? []).map((x) => x.author.name),
    d.job.customerName,
  ];
  return new Set(names.filter((x): x is string => !!x).map(first));
}

/** "Sam, Lee" → "Sam and Lee"; three: "Sam, Lee and Kit"; four or more: "Sam and 3 others". */
export function techWords(list: string): string {
  const t = list
    .split(/,\s*/)
    .map((x) => x.trim())
    .filter(Boolean);
  if (!t.length) return 'nobody on it yet';
  if (t.length === 1) return t[0]!;
  if (t.length > 3) return `${t[0]} and ${t.length - 1} others`;
  return `${t.slice(0, -1).join(', ')} and ${t[t.length - 1]}`;
}

const NO_TECH = 'No tech yet';
const techsOf = (v: JobVisit) => (v.techs === NO_TECH ? NO_TECH : techWords(v.techs));

/** A visit's state in a word, as Visits' rows end: "Booked, confirmed", "Under way", "Done". */
const PAUSE_KEYS = new Set<string>(PAUSE_REASONS.map(([k]) => k));

export function visitStateWord(v: JobVisit, s: JobState): string {
  if (v.drawn === 'booked')
    return v.instructions?.startsWith('Confirmed') ? 'Booked, confirmed' : 'Booked, not confirmed';
  // a paused visit, by why the tech paused it (the tech app's reasons)
  if (v.drawn === 'paused') {
    if (v.pauseReason === 'work_done') return 'Done';
    if (v.pauseReason === 'needs_parts') return s === 'onhold' ? 'Paused for parts' : 'Done, needed parts';
    // the tech app's other reasons, in its words ("Paused: end of day"); a reason it doesn't know, as before
    if (v.pauseReason && PAUSE_KEYS.has(v.pauseReason)) return v.what.split(' · ')[0]!;
    return s === 'onhold' ? 'Paused' : 'Done, needed parts';
  }
  if (v.drawn === 'working') return 'Under way';
  if (v.drawn === 'cancelled') return 'Cancelled';
  return 'Done';
}

/** Who's looking, as far as the record cares: the owner sees money's cost, the dispatcher the board. */
export function viewerOf(d: JobPageData): RecordViewer {
  if (d.can.seeCost) return 'owner';
  if (d.can.dispatch) return 'dispatcher';
  return 'csr';
}

// ── the state line ──

/**
 * Where it stands, at the top only when the strip can't say it (round 3, Amir 26 Oct: "why say Booked
 * and all that when we have the little diagram"): the strip has the dates and the next booked visit, so
 * booked and under way say nothing; the rest one word (On hold, Paid, Cancelled, waiting for review, no
 * visit booked yet), money owing in bold, a problem in red. `word` is the bar's that sticks on scroll.
 */
function recordOf(view: JobView, d: JobPageData, o: ViewOptions, rows: JobVisit[]): RecordState {
  const s = view.state;
  const word = view.standing.word;
  const live = rows.filter((v) => v.drawn !== 'cancelled');
  const inv = d.job.invoice;
  switch (s) {
    case 'booked':
      // no visit booked: nothing on the strip says what's next
      return live.some((v) => v.drawn === 'booked') ? { word } : { word, status: word };
    case 'onhold':
      return { word, status: word };
    case 'underway':
      return { word };
    case 'review': {
      const n = view.review?.left ?? 0;
      const reviewer = !!d.can.complete && !o.mirroring;
      return {
        word,
        status: word,
        ...(n && reviewer
          ? {
              problem: {
                text: `${plural(n, 'thing')} to clear`,
                link: { id: 'review', label: 'Review', target: 'review' },
              },
            }
          : {}),
      };
    }
    case 'unpaid':
      return {
        word,
        ...(inv && inv.balanceCents > 0 && view.invoice ? { owing: `${money(inv.balanceCents)} owing` } : {}),
      };
    case 'paid':
      return { word, status: 'Paid' };
    case 'cancelled':
      return { word, status: word };
  }
}

// ── visits ──

function visitRow(v: JobVisit, s: JobState, fromVisit: string | null | undefined): RecordVisit {
  return {
    id: v.id,
    gutter: `Visit ${v.n}`,
    // when, then who: "Mon Oct 26, 8 a.m.–4 p.m., Brad and Micah"
    line: `${dayWords(v.day)}, ${v.window}, ${techsOf(v)}`,
    // its state is the row's end; under the line only what that doesn't say: a cancelled visit's why, in
    // the words given (QA-777), and what else its state didn't say
    sub: [v.cancelReason, v.also].filter(Boolean).join('; '),
    word: visitStateWord(v, s),
    ...(v.how
      ? { problem: v.how }
      : v.drawn === 'booked' && v.techs === NO_TECH
        ? { problem: 'Nobody on it yet' }
        : {}),
    ...(fromVisit === v.id ? { cameFrom: true } : {}),
  };
}

/** A visit opened in place under its row: the row says when, who and what, so its entry doesn't again. */
export function inPlaceVisit(d: RecordDetail): RecordDetail {
  const said = new Set(['Arrival window', 'Techs', 'Where it stands']);
  const sections = d.sections
    .map((x) =>
      x.heading === 'When and who' && x.facts
        ? { facts: x.facts.filter((f) => !(typeof f.label === 'string' && said.has(f.label))) }
        : x,
    )
    .filter((x) => !x.facts || x.facts.length || x.quote || x.text || x.table || x.photos);
  return { ...d, sections };
}

function visitDetail(v: JobVisit, s: JobState, gettingIn: string | undefined): RecordDetail {
  const word = visitStateWord(v, s);
  const sections: DetailSection[] = [
    {
      heading: 'When and who',
      facts: [
        { label: 'Arrival window', value: `${dayWords(v.day)}, ${v.window}` },
        { label: 'Techs', value: techsOf(v) },
        { label: 'Where it stands', value: v.what },
        ...(v.drawn === 'booked'
          ? [{ label: 'Confirmation', value: v.instructions ?? 'Not confirmed with the customer yet' }]
          : []),
      ],
    },
  ];
  // how the tech gets in, on a visit still to come or under way
  if (gettingIn && v.drawn !== 'done' && v.drawn !== 'cancelled')
    sections.push({ heading: 'Getting in', quote: gettingIn });
  if (v.times?.length)
    sections.push({
      heading: 'Time on site',
      table: {
        label: `Visit ${v.n}’s times`,
        columns: [
          { id: 'tech', label: 'Tech' },
          { id: 'otw', label: 'On the way' },
          { id: 'arr', label: 'Arrived' },
          { id: 'stop', label: 'Stopped' },
          { id: 'on', label: 'On site', align: 'end' },
        ],
        rows: v.times.map((t) => ({
          tech: t.tech,
          otw: t.onTheWay,
          arr: t.arrived,
          stop: t.stopped,
          on: t.onSite,
        })),
      },
    });
  return {
    id: v.id,
    kind: 'visit',
    label: `Visit ${v.n}, ${dayWords(v.day)}`,
    title: `Visit ${v.n}, ${dayWords(v.day)}, ${v.window}`,
    sub: `${techsOf(v)}, ${lower(word)}`,
    ...(v.how ? { note: v.how } : {}),
    sections,
    primary: null,
    // See it on the board first, then the visit's own (Cancel this visit)
    links: [...v.actions].sort((a, b) => (a.id === 'board' ? -1 : b.id === 'board' ? 1 : 0)),
  };
}

// ── estimates ──

const ESTIMATE_STATE: Record<string, RecordEstimate['state']> = {
  draft: 'draft',
  presented: 'presented',
  sent: 'presented',
  sold: 'sold',
  declined: 'declined',
  expired: 'expired',
};

function estimateCards(view: JobView, d: JobPageData): RecordEstimate[] {
  const list = d.estimates?.estimates ?? [];
  return list.map((e: Estimate, i) => {
    const r1 = view.estimates?.list.find((x) => x.id === e.id) ?? view.estimates?.list[i];
    const chosen = e.status === 'sold' ? e.selectedOptionId : (e.waitingSale?.optionId ?? null);
    const state: RecordEstimate['state'] =
      e.status !== 'sold' && e.waitingSale ? 'chosen' : (ESTIMATE_STATE[e.status] ?? 'draft');
    const waits = e.options.some((x) => x.margin?.waits && !x.margin.approved);
    return {
      id: e.id,
      number: `Estimate ${e.number}`,
      title: plainText(e.title) || 'Estimate',
      state,
      ...(e.status === 'sold' && e.disposition === 'sold_later' ? { word: 'Sold, do the work later' } : {}),
      options: e.options.map((x, j) => ({
        id: x.id,
        letter: String.fromCharCode(65 + j),
        ...(x.tier ? { tier: x.tier } : {}),
        title: plainText(x.title) || x.tier || 'Option',
        price: money(x.totalCents),
        ...(chosen === x.id ? { isChosen: true } : {}),
      })),
      line: r1?.foot ?? '',
      ...(waits
        ? {
            problem: d.can.approvePrice
              ? 'A price is under the price book: it needs your OK before it’s sold'
              : 'A price is under the price book: it waits for the office’s OK',
          }
        : {}),
      actions: r1?.actions ?? [],
    };
  });
}

function estimateDetail(c: RecordEstimate): RecordDetail {
  return {
    id: c.id,
    kind: 'estimate',
    label: `${c.number}, ${c.title}`,
    title: `${c.number}: ${c.title}`,
    ...(c.line ? { sub: c.line } : {}),
    ...(c.problem ? { note: c.problem } : {}),
    sections: [
      {
        heading: 'Options',
        table: {
          label: `${c.number}’s options`,
          columns: [
            { id: 'o', label: 'Option' },
            { id: 't', label: 'What' },
            { id: 'p', label: 'Price', align: 'end' },
          ],
          rows: c.options.map((o) => ({
            o: `${o.letter}${o.isChosen ? (c.state === 'sold' ? ', signed' : ', chosen') : ''}`,
            t: o.title,
            p: o.price,
          })),
        },
      },
    ],
    links: c.actions,
  };
}

// ── photos and forms ──

/** "Sun Oct 25, 9:24 a.m.": when a photo was taken, under its tile. */
const takenWords = (iso: string, o: ViewOptions) =>
  `${dayWords(dateText(iso, o.timeZone, { weekday: 'short', year: false, now: o.now }))}, ${timeText(iso, o.timeZone)}`;

/**
 * The job's files as the page shows them (round 3): every one "All photos and files" showed
 * (attachments.list, the signature aside), each once. A picture, or one still on its way up, is a tile
 * in the roll; a video, a PDF, a RAW photo and any other file is a row under it that opens the file.
 */
export function jobFilesShown(
  all: readonly PageAttachment[],
  photos: readonly Photo[],
  o: ViewOptions,
): { roll: Photo[]; files: (RecordFormRow & { iso: string })[] } {
  const { strip, others } = jobFiles(all);
  const names = namesOf([...strip, ...others]);
  const made = new Map(photos.map((p) => [p.id, p]));
  const iso = (a: PageAttachment) => a.capturedAt ?? '';
  const newest = [...strip, ...others].sort((a, b) => iso(b).localeCompare(iso(a)));
  const roll: Photo[] = [];
  const files: (RecordFormRow & { iso: string })[] = [];
  for (const a of newest) {
    const tile = tileOf(a);
    const when = a.capturedAt ? takenWords(a.capturedAt, o) : undefined;
    if (a.kind === 'photo' && (tile.t === 'picture' || tile.t === 'waiting')) {
      const p: Photo = made.get(a.id) ?? {
        id: a.id,
        ...pictureOf(a),
        ...(tile.t === 'waiting' ? { waiting: waitingOf(a) ?? tile.words } : {}),
        caption: plainText(a.caption) || (names.get(a.id) ?? 'A photo'),
      };
      roll.push({ ...p, ...(when ? { taken: when } : {}) });
      continue;
    }
    const href = tile.t === 'file' ? tile.href : a.url;
    files.push({
      id: a.id,
      iso: iso(a),
      at: a.capturedAt ? timeText(a.capturedAt, o.timeZone) : '',
      title: names.get(a.id) ?? 'A file',
      // who uploaded it, else the office: never a role's key
      sub: [
        a.addedBy ?? 'Office',
        a.capturedAt
          ? dayWords(dateText(a.capturedAt, o.timeZone, { weekday: 'short', year: false, now: o.now }))
          : null,
      ]
        .filter(Boolean)
        .join(', '),
      word: tile.t === 'video' ? 'Open the video' : 'words' in tile ? tile.words : 'Open the file',
      glyph: 'form',
      ...(href ? { href } : {}),
    });
  }
  return { roll, files };
}

function photosForms(
  view: JobView,
  d: JobPageData,
  o: ViewOptions,
  details: Record<string, RecordDetail>,
): JobRecordView['photosForms'] {
  const F = view.photos;
  if (!F) return undefined;
  const tz = o.timeZone;
  const shown = jobFilesShown(d.photos?.attachments ?? [], F.photos, o);
  const rows: (RecordFormRow & { iso: string })[] = [...shown.files];
  for (const sub of d.forms?.submissions ?? []) {
    const f = formAnswers(sub);
    const bad = f.rows.filter((r) => r.answer === 'Not answered');
    const row: RecordFormRow = {
      id: sub.id,
      at: timeText(sub.submittedAt, tz),
      title: f.name,
      sub: [
        personName(sub.submittedBy),
        dayWords(dateText(sub.submittedAt, tz, { weekday: 'short', year: false, now: o.now })),
      ]
        .filter(Boolean)
        .join(', '),
      word: sub.complete
        ? 'Complete'
        : bad.length
          ? `${plural(bad.length, 'question')} not answered`
          : 'Not finished',
      ...(sub.complete ? {} : { isBad: true }),
    };
    rows.push({ ...row, iso: sub.submittedAt });
    details[sub.id] = {
      id: sub.id,
      kind: 'form',
      label: f.name,
      title: f.name,
      sub: row.sub,
      ...(sub.complete
        ? {}
        : { note: bad.length ? `${plural(bad.length, 'question')} not answered yet` : 'Not finished yet' }),
      sections: [
        {
          facts: f.rows.map((r) => ({
            label: r.label,
            value: r.answer,
            ...(r.answer === 'Not answered' ? { bad: true } : {}),
          })),
        },
      ],
    };
  }
  for (const g of d.job.signatures ?? []) {
    const id = `sig-${g.signatureId}`;
    const sig = F.signatures.find((x) => x.id === g.signatureId);
    rows.push({
      id,
      iso: g.signedAt,
      at: timeText(g.signedAt, tz),
      title: 'Signed for the work',
      sub: `${g.signerName}, ${g.channel === 'device' ? 'on the iPad' : 'from the invoice’s link'}`,
      word: 'Signed',
      glyph: 'pencil',
    });
    details[id] = {
      id,
      kind: 'signature',
      label: `${g.signerName}’s signature`,
      title: 'Signed for the work',
      sub: sig?.meta ?? g.signerName,
      sections: [
        {
          facts: [
            { label: 'Signed by', value: g.signerName },
            ...(g.invoiceNumber ? [{ label: 'For', value: `The work on ${g.invoiceNumber}` }] : []),
            {
              label: 'Where',
              value: g.channel === 'device' ? 'On the iPad, at the job' : 'From the invoice’s link',
            },
          ],
        },
        ...(sig?.src
          ? [
              {
                photos: [
                  { id, src: sig.src, caption: sig.alt, by: g.signerName, where: 'The signature' } as Photo,
                ],
              },
            ]
          : [{ text: sig?.alt ?? `${g.signerName} signed.` }]),
      ],
    };
  }
  // the gallery is only an editor now (each photo's note): for whoever may change them
  const actions =
    d.photos && d.can.editPhotos && !o.mirroring && shown.roll.length
      ? [{ id: 'gallery', label: 'Edit the photos’ notes' }]
      : [];
  return {
    summary: F.summary,
    photos: shown.roll,
    // newest first, as the roll
    files: rows.sort((a, b) => b.iso.localeCompare(a.iso)).map(({ iso: _iso, ...r }) => r),
    actions,
  };
}

// ── notes and messaging ──

function threadOf(
  view: JobView,
  d: JobPageData,
  o: ViewOptions,
  details: Record<string, RecordDetail>,
): JobRecordView['thread'] {
  const tz = o.timeZone;
  const today = dayWords(dateText(o.now, tz, { weekday: 'short', year: false, now: o.now }));
  const dayOf = (iso: string) => {
    const w = dayWords(dateText(iso, tz, { weekday: 'short', year: false, now: o.now }));
    return w === today ? `Today · ${w}` : w;
  };
  const all: (Message & { iso: string })[] = [];
  // who a text goes to: the contact the composer texts ("Text Jordan"), else the customer
  const textMode = view.history.modes.find((m) => m.id === 'text');
  const customerFirst = textMode?.label.replace(/^Text /, '') || first(d.job.customerName);
  const techs = [...new Set(d.more.visits.flatMap((v) => v.techs.map((t) => first(t.name))))];
  for (const n of d.job.notes ?? [])
    all.push({
      id: `n-${n.id}`,
      iso: n.at,
      who: n.author,
      channel: 'note',
      at: timeText(n.at, tz),
      day: dayOf(n.at),
      text: plainText(n.body),
    });
  for (const m of d.messages?.messages ?? []) {
    const iso = m.receivedAt ?? m.sentAt ?? m.queuedAt;
    const inbound = m.direction === 'in';
    const failed = m.status === 'failed';
    all.push({
      id: `m-${m.id}`,
      iso,
      who: inbound ? (m.customerName ?? (m.from ? phoneText(m.from) : 'The customer')) : 'The office',
      ...(inbound ? {} : { to: customerFirst }),
      channel: inbound ? 'text-in' : m.channel === 'email' ? 'email-out' : 'text-out',
      at: timeText(iso, tz),
      day: dayOf(iso),
      text: m.subject ? `${m.subject}: ${m.body}` : m.body,
      ...(!inbound && !failed ? { status: m.status.replace(/_/g, ' ') } : {}),
      ...(failed ? { problem: `Didn’t send${m.errorMessage ? `: ${m.errorMessage}` : ''}` } : {}),
    });
  }
  for (const m of d.techThread?.messages ?? []) {
    const who = m.author.name ?? (m.fromOffice ? 'The office' : 'A tech');
    all.push({
      id: `j-${m.id}`,
      iso: m.sentAt,
      who,
      ...(m.fromOffice && techs.length ? { to: techWords(techs.join(', ')) } : {}),
      channel: m.fromOffice ? 'techs-out' : 'techs-in',
      at: timeText(m.sentAt, tz),
      day: dayOf(m.sentAt),
      text: m.body,
    });
  }
  all.sort((a, b) => a.iso.localeCompare(b.iso));
  const messages: Message[] = all.map(({ iso: _iso, ...m }) => m);
  const last = messages[messages.length - 1];
  const waiting = last?.channel === 'text-in';
  if (waiting) messages[messages.length - 1] = { ...last!, problem: 'No answer yet' };
  for (const m of messages)
    details[m.id] ??= {
      id: m.id,
      kind: m.channel === 'note' ? 'note' : 'message',
      label: `${m.who}, ${m.day.replace('Today · ', '')}, ${m.at}`,
      title: m.channel === 'note' ? `A note from ${m.who}` : `${m.who}${m.to ? ` to ${m.to}` : ''}`,
      sub: `${m.day.replace('Today · ', '')}, ${m.at}`,
      sections: [
        { quote: m.text },
        ...(m.status ? [{ facts: [{ label: 'How it went', value: m.status }] }] : []),
      ],
    };
  return {
    summary: waiting
      ? `${last!.who} wrote and nobody has answered: “${last!.text.length > 80 ? `${last!.text.slice(0, last!.text.lastIndexOf(' ', 80))}…` : last!.text}”`
      : messages.length
        ? `${plural(messages.length, 'so far', 'so far')}, the last ${last!.day.replace('Today · ', 'today, ')}`
        : 'Nothing written on the job yet',
    messages,
    modes: view.history.modes,
    ...(waiting ? { isWaiting: true } : {}),
  };
}

// ── history: each row opens in place with what its line doesn't say ──

const INVOICE_STATE: Record<string, string> = {
  draft: 'Draft',
  finalized: 'Finalized',
  sent: 'Sent',
  partially_paid: 'Part paid',
  paid: 'Paid in full',
  void: 'Void',
};
const qtyText = (q: string) => String(Number(q));

/** An estimate's state in words, as its History card says it: "Sold: option B, $3,640.00". */
function estimateWords(c: RecordEstimate): string {
  const chosen = c.options.find((o) => o.isChosen);
  switch (c.state) {
    case 'sold':
      return chosen ? `Sold: option ${chosen.letter}, ${chosen.price}` : 'Sold';
    case 'chosen':
      return chosen
        ? `Chosen: option ${chosen.letter}, ${chosen.price}; not signed yet`
        : 'Chosen; not signed yet';
    case 'presented':
      return 'Presented; waiting for the customer';
    case 'declined':
      return 'Declined';
    case 'expired':
      return 'Expired';
    default:
      return 'Draft';
  }
}

/**
 * History's rows, each opening in place as a card made for its type (Amir and Brook, 26 Oct): an invoice
 * in short, a payment with what it was on and what's owing after, an estimate's options and where it
 * stands, a visit's when, who and time on site, a call's or a text's words and how it went; each with
 * the link to its full record. A row whose line says it all opens to nothing. Money says its invoice.
 */
function historyDetails(
  view: JobView,
  d: JobPageData,
  o: ViewOptions,
  rows: JobVisit[],
  cards: RecordEstimate[],
  details: Record<string, RecordDetail>,
): JobEntry[] {
  const events = d.timeline?.events ?? [];
  const photos = new Map((view.photos?.photos ?? []).map((p) => [p.id, p]));
  const visitN = new Map(rows.map((v) => [v.id, v]));
  const estimateOf = new Map((d.estimates?.estimates ?? []).map((e) => [e.id, e]));
  const messages = new Map((d.messages?.messages ?? []).map((m) => [`m-${m.id}`, m]));
  const notes = new Map((d.job.notes ?? []).map((n) => [`n-${n.id}`, n]));
  const techMsgs = new Map((d.techThread?.messages ?? []).map((m) => [`j-${m.id}`, m]));
  // the money only where the page shows it (the round-1 view's invoice: whoever may see it)
  const invoices = view.invoice ? d.more.invoices : [];
  const invoiceBy = (id?: string) =>
    invoices.find((i) => i.id === id) ??
    (id ? undefined : (invoices.find((i) => i.isPrimary) ?? invoices[0]));
  const invoiceNamed = (number: string) => invoices.find((i) => i.number === number);
  const miniOf = (id?: string): MiniInvoiceView | null => {
    const i = invoiceBy(id);
    if (!i) return null;
    const lines = d.job.invoice?.id === i.id ? d.job.invoice.lines : [];
    const paid = i.totalCents - i.balanceCents;
    return {
      number: i.number,
      status: INVOICE_STATE[i.status] ?? i.status,
      ...(i.status === 'void' ? { isBad: true } : {}),
      lines: lines.slice(0, 3).map((l) => ({
        id: l.id,
        item: plainText(l.description),
        qty: qtyText(l.quantity),
        amount: money(l.totalCents),
      })),
      ...(lines.length > 3 ? { more: lines.length - 3 } : {}),
      total: money(i.totalCents),
      ...(paid > 0 && i.status !== 'void' ? { paid: `−${money(paid)}` } : {}),
      owing: money(i.status === 'void' ? 0 : i.balanceCents),
    };
  };
  const openInvoice = (id: string, label = 'Open the invoice'): ActionLink => ({
    id: 'invoice',
    label,
    target: id,
  });
  const goVisit = (id: string): ActionLink | null => {
    const v = visitN.get(id);
    return v ? { id: 'visit', label: `Go to visit ${v.n}`, target: `detail:${v.id}` } : null;
  };
  /** A visit's card: when, who, where it stands and its time on site (its detail's sections). */
  const visitCard = (id: string, sections: DetailSection[], push: (l: ActionLink | null) => void) => {
    const vd = details[id];
    if (!vd) return;
    sections.push(...vd.sections.filter((s) => s.heading !== 'Getting in'));
    push(goVisit(id));
  };
  const whenOf = (iso: string) =>
    `${dayWords(dateText(iso, o.timeZone, { weekday: 'short', year: false, now: o.now }))}, ${timeText(iso, o.timeZone)}`;
  const numbers = invoices.map((i) => i.number);
  const namesAnInvoice = (t: string) => numbers.some((n) => t.includes(n));
  const out: JobEntry[] = [];
  for (const e of view.history.entries) {
    const sections: DetailSection[] = [];
    const links: ActionLink[] = [];
    const push = (l: ActionLink | null) => {
      if (l) links.push(l);
    };
    const t = /^t-(\d+)-/.exec(e.id);
    const ev = t ? events[Number(t[1])] : undefined;
    let row: JobEntry = e;
    if (e.id === 'k-booked') {
      const v1 = rows[0];
      if (v1) visitCard(v1.id, sections, push);
    } else if (e.id === 'k-completed') {
      const mini = miniOf();
      if (mini) {
        sections.push({ invoice: mini });
        push(openInvoice(invoiceBy()!.id));
        // the money on the row names its invoice: "INV-18402 · $3,322.00 owing"
        row = { ...e, detail: `${mini.number} · ${mini.owing} owing` };
      }
    } else if ((e.id === 'k-cancelled' || e.id === 'k-sentback') && e.quote)
      sections.push({ heading: 'Why', quote: e.quote });
    else if (notes.has(e.id)) {
      sections.push({ quote: plainText(notes.get(e.id)!.body) });
      sections.push({
        facts: [{ label: 'Who sees it', value: 'The office and the techs on the job' }],
      });
    } else if (messages.has(e.id)) {
      const m = messages.get(e.id)!;
      const inbound = m.direction === 'in';
      sections.push({ quote: m.subject ? `${m.subject}: ${m.body}` : m.body });
      sections.push({
        facts: [
          inbound
            ? {
                label: 'From',
                value: m.from ? (m.channel === 'sms' ? phoneText(m.from) : m.from) : 'The customer',
              }
            : { label: 'To', value: m.channel === 'sms' ? phoneText(m.to) : m.to },
          ...(!inbound
            ? [
                {
                  label: 'How it went',
                  value:
                    m.status === 'failed'
                      ? `Didn’t send${m.errorMessage ? `: ${m.errorMessage}` : ''}`
                      : m.status.replace(/_/g, ' '),
                  ...(m.status === 'failed' ? { bad: true } : {}),
                },
              ]
            : []),
        ],
      });
      push(
        inbound
          ? { id: 'reply', label: 'Reply in Notes and messaging', target: 'section:thread' }
          : { id: 'thread', label: 'See it in Notes and messaging', target: 'section:thread' },
      );
    } else if (techMsgs.has(e.id)) {
      sections.push({ quote: techMsgs.get(e.id)!.body });
      push({ id: 'thread', label: 'See it in Notes and messaging', target: 'section:thread' });
    } else if (ev) {
      if (ev.entityType === 'call') {
        // a call: which way and how long, what came of it, who took it (a call has no page of its own)
        sections.push({
          facts: [
            { label: 'Call', value: ev.title.replace(' · ', ', ') },
            ...(ev.detail ? [{ label: 'What came of it', value: ev.detail }] : []),
            ...(ev.who ? [{ label: 'Answered by', value: ev.who }] : []),
          ],
        });
      } else if (ev.entityType === 'appointment') visitCard(ev.entityId, sections, push);
      else if (ev.entityType === 'estimate') {
        const est = estimateOf.get(ev.entityId);
        const card = cards.find((c) => c.id === ev.entityId);
        if (est && card) {
          sections.push({ facts: [{ label: 'Where it stands', value: estimateWords(card) }] });
          sections.push(...(details[card.id]?.sections ?? []));
          if (card.problem) sections.push({ text: card.problem });
          const open = card.actions.find((a) => a.id === 'estimate-open');
          push(
            open
              ? { ...open, label: 'Open the estimate' }
              : { id: 'estimate', label: `Go to estimate ${est.number}`, target: `detail:${est.id}` },
          );
        }
      } else if (ev.entityType === 'invoice') {
        const mini = miniOf(ev.entityId);
        if (mini) {
          sections.push({ invoice: mini });
          push(openInvoice(ev.entityId));
          if (!namesAnInvoice(`${e.text}`))
            row = { ...e, detail: e.detail ? `${mini.number} · ${e.detail}` : mini.number };
        }
      } else if (ev.entityType === 'payment' && invoices.length) {
        // "Payment on INV-18402 · e-Transfer": the amount, how, who recorded it, on which invoice, and
        // what's owing on it now
        const m = /^Payment on (\S+)(?: · (.*))?$/.exec(ev.title);
        const i = m ? invoiceNamed(m[1]!) : undefined;
        const amount = ev.amountCents != null ? money(ev.amountCents) : null;
        sections.push({
          facts: [
            ...(amount ? [{ label: 'Amount', value: amount }] : []),
            ...(m?.[2] ? [{ label: 'Method', value: m[2] }] : []),
            {
              label: 'Recorded',
              value: `${ev.who && ev.who !== 'Someone' ? `${ev.who}, ` : ''}${whenOf(ev.at)}`,
            },
            ...(i ? [{ label: 'Invoice', value: i.number }] : []),
            ...(ev.detail ? [{ label: 'Status', value: ev.detail, bad: true }] : []),
            ...(i
              ? [
                  {
                    label: 'Owing on it now',
                    value: i.balanceCents > 0 ? money(i.balanceCents) : '$0.00: paid in full',
                  },
                ]
              : []),
          ],
        });
        if (i) push(openInvoice(i.id, 'Go to the invoice'));
        if (amount && i)
          row = { ...e, head: `${amount} paid on ${i.number}${m?.[2] ? `, ${lower(m[2])}` : ''}` };
      }
      if (ev.kind === 'photo' && photos.has(ev.entityId))
        sections.push({ photos: [photos.get(ev.entityId)!] });
      if (ev.kind === 'form' && details[ev.entityId]) {
        const f = details[ev.entityId]!;
        sections.push(...f.sections);
      }
      if (ev.kind === 'material') push({ id: 'section', label: 'Go to the parts', target: 'section:parts' });
    }
    // any other money on a row says which invoice it's about, when the job has one
    const said = `${typeof row.head === 'string' ? row.head : ''} ${row.detail ?? ''}`;
    if (/\$\d/.test(said) && !namesAnInvoice(said) && invoices.length === 1)
      row = { ...row, detail: row.detail ? `${row.detail} · ${invoices[0]!.number}` : invoices[0]!.number };
    if (sections.length || links.length) {
      const title = typeof row.head === 'string' ? row.head : row.text;
      details[e.id] = {
        id: e.id,
        kind: e.kind,
        label: title,
        title,
        sections,
        links,
      };
    }
    // a note's and a text's words live in Notes and messaging (and in the open entry), not on the row
    out.push(
      row.kind === 'text' || row.kind === 'note' || row.id === 'k-cancelled' || row.id === 'k-sentback'
        ? { ...row, quote: undefined }
        : row,
    );
  }
  return out;
}

// ── the strip ──

function stripOf(
  view: JobView,
  rows: JobVisit[],
  s: JobState,
  entries: JobEntry[],
  people: Set<string>,
): JobRecordView['strip'] {
  const next = rows.find((v) => v.drawn === 'booked');
  // the booking is its own stop only until a visit is booked: then the crumb, the head and History say
  // who booked it, and its time (when the booking was keyed in) would sit out of order among the visits
  const hasVisit = rows.some((v) => v.drawn !== 'cancelled');
  const stops: StripStop[] = view.strip.stops
    .filter((x) => !(hasVisit && x.id === 'k-booked'))
    .map((x) => {
      const visit = x.label.startsWith('Visit ') ? rows.find((v) => x.label === `Visit ${v.n}`) : undefined;
      const e = entries.find((h) => h.id === x.id);
      const what = x.says.split(' · ')[0]!;
      const card: KeyValueItem[] | undefined = visit
        ? [
            { label: 'Window', value: `${dayWords(visit.day)}, ${visit.window}` },
            { label: 'Techs', value: techsOf(visit) },
            { label: 'Where it stands', value: visit.what },
          ]
        : [
            { label: 'What', value: what },
            ...(x.says.includes(' · ')
              ? [{ label: 'When', value: x.says.split(' · ').slice(1).join(', ') }]
              : []),
          ];
      const who = typeof e?.head === 'string' ? e.head.split(' ')[0] : undefined;
      const booked = visit?.drawn === 'booked';
      const isNext = booked && visit === next;
      return {
        ...x,
        // the strip carries the dates: every visit its own day
        ...(visit ? { day: dayWords(visit.day) } : x.day ? { day: dayWords(x.day) } : {}),
        // the next booked visit, marked next, with its window and techs (round 3)
        ...(isNext ? { label: `${x.label} · next` } : {}),
        ...(visit
          ? booked
            ? { sub: `${visit.window}, ${techsOf(visit)}` }
            : { sub: `${techsOf(visit)}, ${lower(visitStateWord(visit, s))}` }
          : who && people.has(who)
            ? { sub: who }
            : {}),
        ...(booked ? { isNext: true } : {}),
        card,
        opens: visit ? visit.id : x.id,
      };
    });
  // the latest four fit beside Full history at 1280, which has the rest; the first carries its day
  const keep = 4;
  if (stops.length <= keep) return { stops, isWaiting: view.strip.isWaiting };
  const cut = stops.length - keep;
  const kept = stops.slice(cut);
  const day = kept[0]!.day ?? [...stops.slice(0, cut)].reverse().find((x) => x.day)?.day;
  return {
    stops: [{ ...kept[0]!, ...(day ? { day } : {}) }, ...kept.slice(1)],
    isWaiting: view.strip.isWaiting,
  };
}

// ── the record ──

/** What starts open: Customer, Site, Notes and messaging and History always, then the state's. */
function openOf(view: JobView, d: JobPageData, o: ViewOptions, cards: RecordEstimate[]): string[] {
  const s = view.state;
  const open = new Set(['customer', 'site', 'thread', 'history']);
  const reviewer = !!d.can.complete;
  const byState: Record<JobState, string[]> = {
    booked: ['summary', 'visits'],
    onhold: ['visits', 'parts'],
    underway: ['visits'],
    review: reviewer ? ['review', 'photos'] : [],
    unpaid: view.invoice ? ['invoice'] : [],
    paid: [],
    cancelled: [],
  };
  for (const id of byState[s]) open.add(id);
  if (
    s !== 'paid' &&
    s !== 'unpaid' &&
    cards.some((c) => c.state === 'presented' || c.state === 'chosen' || !!c.problem)
  )
    open.add('estimates');
  if (o.fromVisit && s !== 'cancelled') open.add('visits');
  if (view.side.tasks?.rows.some((t) => !t.isDone)) open.add('tasks');
  return [...open];
}

/** Blueprint's job record, from the round-1 view and the read it came from. */
export function toJobRecordView(view: JobView, page: JobPageData, o: ViewOptions): JobRecordView {
  const d = namedPeople(page);
  const s = view.state;
  const rows = view.visits.rows;
  const details: Record<string, RecordDetail> = {};
  const gettingIn = view.side.site.gettingIn;
  for (const v of rows) details[v.id] = visitDetail(v, s, gettingIn);
  const cards = estimateCards(view, d);
  for (const c of cards) details[c.id] = estimateDetail(c);
  const pf = photosForms(view, d, o, details);
  const thread = threadOf(view, d, o, details);
  const entries = historyDetails(view, d, o, rows, cards, details);
  // in place under its row, a visit says only what its row doesn't (History's card keeps them all)
  for (const v of rows) details[v.id] = inPlaceVisit(details[v.id]!);
  const jobType = d.more.jobType?.name;
  const {
    standing: _standing,
    visits: _visits,
    estimates: _estimates,
    summary,
    strip: _strip,
    history,
    ...rest
  } = view;
  const copy = { id: 'copy', label: 'Copy a link to this job' };
  const menu = [
    { items: [copy] },
    ...view.menu
      .map((m) => ({ ...m, items: m.items.filter((x) => x.id !== 'copy') }))
      .filter((m) => m.items.length),
  ];
  return {
    ...rest,
    viewer: viewerOf(d),
    // one quiet line under who and where: the type and the number (the title is the scope, so the type
    // only when it says something more)
    meta: jobType && jobType !== view.title ? `${jobType} · Job ${view.number}` : `Job ${view.number}`,
    record: recordOf(view, d, o, rows),
    menu,
    strip: stripOf(view, rows, s, entries, peopleOf(d)),
    ...(summary
      ? {
          summary: {
            line: summary.line,
            items: summary.items.map((x) => ({ label: x.label, value: x.value })),
          },
        }
      : {}),
    visits: {
      summary: view.visits.summary,
      rows: rows.map((v) => visitRow(v, s, o.fromVisit)),
      actions: view.visits.actions,
    },
    ...(view.estimates
      ? { estimates: { summary: view.estimates.summary, cards, actions: view.estimates.actions } }
      : {}),
    ...(pf ? { photosForms: pf } : {}),
    thread,
    history: { ...history, entries, filters: RECORD_FILTERS },
    details,
    open: openOf(view, d, o, cards),
    ...(o.fromVisit && details[o.fromVisit] ? { openOnArrival: o.fromVisit } : {}),
    side: {
      ...view.side,
      // the meta lines in words: "Phone, main", not a run of dots
      customer: {
        ...view.side.customer,
        note: view.side.customer.note.replace(/ · /g, ', '),
        contacts: view.side.customer.contacts.map((c) => ({
          ...c,
          ...(c.note ? { note: c.note.replace(/ · /g, ', ') } : {}),
        })),
      },
    },
    state: s,
  };
}

/**
 * The rows kept open from the address: a visit, a form, the signature or a History row that's there and
 * has more to show. Anything else in the address is dropped.
 */
export function openRowsIn(v: JobRecordView, ids: readonly string[]): string[] {
  const rows = new Set([
    ...v.visits.rows.map((x) => x.id),
    ...(v.photosForms?.files ?? []).filter((f) => !f.href).map((f) => f.id),
    ...v.history.entries.map((e) => e.id),
  ]);
  return ids.filter((id) => rows.has(id) && !!v.details[id]);
}
